import React, { useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { Widget, LayoutDefinition } from '../types';
import { allWidgets, widgetsOf } from '../normalize';
import { hasKey, useI18n } from '../../i18n';

interface ObjectListProps {
  layout: LayoutDefinition;
  selectedWidget?: Widget;
  selectedWidgetPath?: number[];
  onWidgetSelect: (widget: Widget, path: number[]) => void;
  onWidgetDelete: (path: number[]) => void;
  compact?: boolean;
}

function widgetIcon(type: string): string {
  if (type === 'container') return '▭';
  if (type === 'label') return 'Aa';
  if (type === 'icon') return '◆';
  if (type === 'button') return '▢';
  if (type === 'switch') return '⏻';
  if (type === 'gauge') return '◎';
  if (type === 'progress') return '▬';
  return '▫️';
}

function widgetCaption(widget: Widget): string {
  return String(widget.id || widget.text || widget.type);
}

function WidgetRow({
  widget,
  path,
  selectedPathKey,
  onSelect,
  onDelete,
  typeLabel,
}: {
  widget: Widget;
  path: number[];
  selectedPathKey?: string;
  onSelect: (widget: Widget, path: number[]) => void;
  onDelete: (path: number[]) => void;
  typeLabel: (type: string) => string;
}) {
  const { t } = useI18n();
  const isSelected = selectedPathKey === path.join('.');
  return (
    <>
      <ListItem
        disablePadding
        sx={{
          pl: path.length > 1 ? (path.length - 1) * 2 : 0,
          backgroundColor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': {
            backgroundColor: isSelected ? 'action.selected' : 'action.hover',
          },
        }}
      >
        <ListItemButton onClick={() => onSelect(widget, path)} sx={{ py: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Typography sx={{ fontSize: '1rem' }}>{widgetIcon(widget.type)}</Typography>
          </ListItemIcon>
          <ListItemText
            primary={(
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">{widgetCaption(widget)}</Typography>
                <Chip
                  label={typeLabel(widget.type)}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.6rem', height: '16px' }}
                />
              </Box>
            )}
            secondary={`${widget.x || 0}, ${widget.y || 0} · ${widget.width || t('layout.auto')}×${widget.height || t('layout.auto')}`}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
          <IconButton
            size="small"
            color="error"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(path);
            }}
          >
            <Delete sx={{ fontSize: '1rem' }} />
          </IconButton>
        </ListItemButton>
      </ListItem>
      {widget.children?.map((child, index) => (
        <WidgetRow
          key={child.id || `${path.join('.')}.${index}`}
          widget={child}
          path={[...path, index]}
          selectedPathKey={selectedPathKey}
          onSelect={onSelect}
          onDelete={onDelete}
          typeLabel={typeLabel}
        />
      ))}
    </>
  );
}

export const ObjectList: React.FC<ObjectListProps> = ({
  layout,
  selectedWidgetPath,
  onWidgetSelect,
  onWidgetDelete,
  compact = false,
}) => {
  const { t } = useI18n();
  const widgets = widgetsOf(layout);
  const count = allWidgets(widgets).length;
  const typeLabel = useCallback((type: string) => (
    hasKey(`layout.widget.${type}`) ? t(`layout.widget.${type}`) : type
  ), [t]);

  return (
    <Paper elevation={0} variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box p={compact ? 1 : 2}>
        <Typography variant={compact ? 'caption' : 'h6'} color={compact ? 'text.secondary' : undefined}>
          {t('layout.objects', { count })}
        </Typography>
        {!compact && (
          <Typography variant="caption" color="textSecondary">
            {t('layout.objectsHint')}
          </Typography>
        )}
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List dense>
          {widgets.map((widget, index) => (
            <WidgetRow
              key={widget.id || index}
              widget={widget}
              path={[index]}
              selectedPathKey={selectedWidgetPath?.join('.')}
              onSelect={onWidgetSelect}
              onDelete={onWidgetDelete}
              typeLabel={typeLabel}
            />
          ))}
        </List>
      </Box>
    </Paper>
  );
};
